/**
 * Het inlogscherm.
 *
 * Gewone HTML, geen canvas. Dat is hier de juiste keuze en niet de luie: een invoerveld op
 * een canvas moet je zelf bouwen — cursor, selectie, plakken, wachtwoordbeheerders,
 * schermlezers, de toetsenbordbalk van een telefoon — en dat levert een slechter veld op
 * dan de browser gratis geeft. De rest van de app doet het al zo: het bord staat op canvas,
 * alles eromheen is HTML.
 *
 * De omlijsting volgt wel de kleuren van het origineel, zodat het geen ander programma
 * lijkt.
 */
import { inloggen, registreren } from './account.js';
import type { Ik } from '@catan/contracts';

const STIJL = `
#inlog {
  position: absolute; inset: 0; z-index: 20; display: grid; place-items: center;
  background: rgba(12, 7, 2, 0.86);
}
/* Op de voordeur staat de titelplaat erachter; die mag je zien. */
#inlog.doorzicht { background: rgba(12, 7, 2, 0.45); }
#inlog .kader { box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6); }
#inlog .kader {
  width: min(26rem, 92vw); padding: 1.2rem 1.4rem;
  background: #17100a; color: #f3e6cf;
  border: 2px solid #c9a227; border-radius: 6px;
  font-family: Georgia, 'Times New Roman', serif;
}
#inlog h2 { margin: 0 0 0.2rem; font-size: 1.1rem; color: #ffd257; }
#inlog p.uitleg { margin: 0 0 0.9rem; font-size: 0.85rem; color: #d8c9a8; }
#inlog label { display: block; margin: 0.5rem 0 0.15rem; font-size: 0.85rem; }
#inlog input {
  width: 100%; padding: 0.4rem 0.5rem; font-family: inherit; font-size: 0.95rem;
  background: #f3e6cf; color: #2b1d0e; border: 2px solid #7a5c33; border-radius: 4px;
}
#inlog .knoppen { display: flex; gap: 0.5rem; margin-top: 1rem; }
#inlog button { flex: 1; text-align: center; font-size: 0.9rem; }
#inlog button.zwak { background: #2c1608; color: #e3cfa3; border-color: #7a5c33; }
#inlog .melding { margin: 0.8rem 0 0; min-height: 1.2rem; font-size: 0.85rem; color: #ffb4a0; }
#inlog .melding.goed { color: #a9dc8f; }
#inlog .wissel {
  margin: 0.9rem 0 0; font-size: 0.8rem; color: #d8c9a8; background: none;
  border: none; padding: 0; cursor: pointer; text-decoration: underline;
}
`;

export interface InlogOpties {
  /** Aangeroepen zodra je binnen bent. */
  readonly onIngelogd: (ik: Ik) => void;
  /** Aangeroepen als je zonder account verder wilt. Weglaten = inloggen is verplicht. */
  readonly onOverslaan?: (() => void) | undefined;
  /** De achtergrondplaat schemert door (de voordeur op het titelscherm). */
  readonly doorzicht?: boolean | undefined;
  /** Een openingszin, bijvoorbeeld "Je sessie is verlopen; log opnieuw in." */
  readonly beginMelding?: string | undefined;
}

/**
 * Zet het inlogscherm neer. Geeft een functie terug die het weer weghaalt.
 *
 * Eén scherm voor inloggen én registreren: je wisselt met een link. Twee losse schermen
 * zouden voor deze twee formulieren van elk drie velden alleen maar meer klikken kosten.
 */
export function inlogscherm(opties: InlogOpties): () => void {
  if (document.getElementById('inlog-stijl') === null) {
    const stijl = document.createElement('style');
    stijl.id = 'inlog-stijl';
    stijl.textContent = STIJL;
    document.head.appendChild(stijl);
  }

  const laag = document.createElement('div');
  laag.id = 'inlog';
  if (opties.doorzicht === true) laag.classList.add('doorzicht');
  document.body.appendChild(laag);

  let modus: 'inloggen' | 'registreren' = 'inloggen';
  let bezig = false;
  let eersteMelding = opties.beginMelding;

  const weg = (): void => {
    laag.remove();
  };

  const teken = (): void => {
    const nieuw = modus === 'registreren';
    laag.innerHTML = `
      <form class="kader" novalidate>
        <h2>${nieuw ? 'Account aanmaken' : 'Inloggen'}</h2>
        <p class="uitleg">${
          nieuw
            ? 'Je hebt een uitnodigingscode nodig. Dit spel is besloten.'
            : 'Welkom terug in Catan.'
        }</p>
        ${
          nieuw
            ? `<label for="inlog-code">Uitnodigingscode</label>
               <input id="inlog-code" name="code" autocomplete="off" autocapitalize="characters"
                      spellcheck="false" maxlength="6" placeholder="ABC234" />`
            : ''
        }
        <label for="inlog-naam">Gebruikersnaam</label>
        <input id="inlog-naam" name="username" autocomplete="username" spellcheck="false" />
        <label for="inlog-wachtwoord">Wachtwoord</label>
        <input id="inlog-wachtwoord" name="password" type="password"
               autocomplete="${nieuw ? 'new-password' : 'current-password'}" />
        <div class="knoppen">
          <button type="submit" id="inlog-verder">${nieuw ? 'Account aanmaken' : 'Inloggen'}</button>
          ${opties.onOverslaan === undefined ? '' : '<button type="button" class="zwak" id="inlog-later">Later</button>'}
        </div>
        <p class="melding" id="inlog-melding" role="status" aria-live="polite"></p>
        <button type="button" class="wissel" id="inlog-wissel">${
          nieuw ? 'Ik heb al een account' : 'Ik heb een uitnodigingscode'
        }</button>
      </form>`;

    const form = laag.querySelector('form');
    const melding = laag.querySelector<HTMLElement>('#inlog-melding');
    const zeg = (tekst: string, goed = false): void => {
      if (melding === null) return;
      melding.textContent = tekst;
      melding.classList.toggle('goed', goed);
    };

    laag.querySelector('#inlog-wissel')?.addEventListener('click', () => {
      modus = nieuw ? 'inloggen' : 'registreren';
      teken();
    });
    laag.querySelector('#inlog-later')?.addEventListener('click', () => {
      weg();
      opties.onOverslaan?.();
    });

    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      if (bezig) return;
      const waarde = (id: string): string =>
        laag.querySelector<HTMLInputElement>(`#${id}`)?.value.trim() ?? '';
      const naam = waarde('inlog-naam');
      const wachtwoord = laag.querySelector<HTMLInputElement>('#inlog-wachtwoord')?.value ?? '';
      if (naam === '' || wachtwoord === '') {
        zeg('Vul allebei de velden in.');
        return;
      }

      bezig = true;
      zeg(nieuw ? 'Account aanmaken…' : 'Even kijken…');
      void (async () => {
        const uit = nieuw
          ? await registreren(waarde('inlog-code'), naam, wachtwoord)
          : await inloggen(naam, wachtwoord);
        bezig = false;
        if (uit.ok) {
          weg();
          opties.onIngelogd(uit.waarde);
          return;
        }
        // De server stuurt een zin mee; die is al in het Nederlands en zegt genoeg. Alleen
        // bij de rem vertellen we er hoe lang bij, want dat scheelt boos klikken.
        zeg(
          uit.wachtSeconden === undefined
            ? uit.bericht
            : `${uit.bericht} (nog ${uit.wachtSeconden} seconden)`,
        );
      })();
    });

    laag.querySelector<HTMLInputElement>(nieuw ? '#inlog-code' : '#inlog-naam')?.focus();
    if (eersteMelding !== undefined) {
      zeg(eersteMelding);
      eersteMelding = undefined;
    }
  };

  teken();
  return weg;
}

/**
 * Het scherm voor als er helemaal geen server is.
 *
 * Zonder server is er geen account, geen potje en geen tegenstander — dit spel ís de
 * server. Een statische uitrol die er half werkend uitziet zou erger zijn dan deze
 * eerlijke mededeling met een knop om het opnieuw te proberen.
 */
export function geenServerScherm(opties: { onOpnieuw: () => void }): () => void {
  if (document.getElementById('inlog-stijl') === null) {
    const stijl = document.createElement('style');
    stijl.id = 'inlog-stijl';
    stijl.textContent = STIJL;
    document.head.appendChild(stijl);
  }
  const laag = document.createElement('div');
  laag.id = 'inlog';
  laag.classList.add('doorzicht');
  document.body.appendChild(laag);
  laag.innerHTML = `
    <div class="kader">
      <h2>De server is niet bereikbaar</h2>
      <p class="uitleg">
        Het Kaartspel van Catan heeft een verbinding met de spelserver nodig — voor je
        account, je potjes en je tegenstanders. Controleer je verbinding en probeer het
        opnieuw.
      </p>
      <div class="knoppen">
        <button type="button" id="inlog-opnieuw">Opnieuw proberen</button>
      </div>
    </div>`;
  laag.querySelector('#inlog-opnieuw')?.addEventListener('click', () => {
    laag.remove();
    opties.onOpnieuw();
  });
  return () => laag.remove();
}
